<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" max-width="940" persistent scrollable><k-card>
	<k-card-title>
		<k-icon color="pink darken-4" class="mr-2" style="margin-top:-4px;">fas fa-robot</k-icon>
		<b class="pink--text text--darken-4">Suggest {{params.mdc.lc_title_plural}}</b>
		<k-spacer/>
	</k-card-title>
	<k-card-text class="mt-1" style="font-size:16px">
		<div v-if="!alba_warning_issued" class="lpc-pe-alba-warning" style="width:700px; margin:0 auto">
			<p>Lesson suggestions are generated by a large language model (LLM), commonly called an artificial intelligence (AI) system, on the basis of the standards you’ve chosen and other information you’ve already specified in your lesson plan.</p>
			<p>Many teachers find these suggestions extremely helpful as they develop their lesson plans. However, please keep in mind that LLMs are <i>not</i> actually “intelligent”, so you should expect these suggestions to vary in quality.</p>
			<p class="mt-3 text-center pink--text text--darken-4" style="font-size:20px;line-height:26px">Remember: <b><i>YOU</i></b> must be the final arbiter <nobr>of what you decide to teach your students!</nobr></p>
			<div class="text-right">
				<k-btn color="pink darken-4" dark @click="alba_warning_issued=true"><k-icon small class="mr-2">fas fa-check</k-icon>Got It!</k-btn>
			</div>
		</div>
		<div v-else>

			<div v-if="params.mdc.lc_suppress_detail_prompt!=true && params.component.lc_suggestion_length > 0" class="lpc-pe-section">
				<div class="d-flex align-start">
					<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" style="margin:2px 6px 0 0; font-size:18px;" color="light-blue" @click="evidence_help('detail')">fas fa-info-circle</k-icon></template>Click for more info</k-tooltip>
					<div class="lpc-pe-section-description">Level of Detail</div>
					<div>Would you like these suggestions to be <nobr><b>more detailed</b>,</nobr> <nobr><b>less detailed</b>,</nobr> <nobr>or <b>about the same</b></nobr> as the last set of suggestions?</div>
					<k-spacer/>
					<div style="width:280px" class="ml-3">
						<k-select v-model="detail_setting" :items="detail_select" dense outlined hide-details append-icon="fas fa-caret-down"></k-select>
					</div>
				</div>
			</div>

			<div v-if="show_topics_options" class="lpc-pe-section">
				<div class="d-flex align-start">
					<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" style="margin:2px 6px 0 0; font-size:18px;" color="light-blue" @click="evidence_help('topics_option')">fas fa-info-circle</k-icon></template>Click for more info</k-tooltip>
					<div class="lpc-pe-section-description">Objectives Format</div>
					<div>Choose the format to use for suggested Objectives/Targets</div>
					<k-spacer/>
					<div style="width:440px" class="ml-3">
						<k-select v-model="topics_option" :items="topics_option_select" dense outlined hide-details append-icon="fas fa-caret-down"></k-select>
					</div>
				</div>
			</div>

			<div class="lpc-pe-section text-center">
				<k-btn small text color="pink darken-4" dark @click="lpc_advanced_settings_showing=!lpc_advanced_settings_showing">Toggle Advanced Settings<k-icon small class="ml-2">fas {{lpc_advanced_settings_showing?'fa-caret-up':'fa-caret-down'}}</k-icon></k-btn>
			</div>

			<div v-if="lpc_advanced_settings_showing" class="mt-2">
				<div class="lpc-pe-alba-warning pt-2 text-center">
					Use the Advanced Settings below only if you want fine-tuned control.<br><b>You can safely ignore these</b>—everything is already set up to work well by default.
				</div>

				<div v-if="all_standards.length > 0" class="lpc-pe-section">
					<div class="d-flex align-start">
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" style="margin:2px 6px 0 0; font-size:18px;" color="light-blue" @click="evidence_help('standards')">fas fa-info-circle</k-icon></template>Click for more info</k-tooltip>
						<div class="lpc-pe-section-description">Standards</div>
						<div>
							<div class="d-flex flex-wrap">
								<div v-for="(s) in all_standards" :key="s.identifier" class="d-flex align-center mr-6 mb-1">
									<k-checkbox class="shrink mt-0 pt-0 d-inline-block" color="pink darken-3" off-icon="far fa-square" on-icon="fas fa-check-square" hide-details v-model="standards_selected[s.identifier]"></k-checkbox>
									<CASEItemBtn @click="show_standard(s)" :btn_color="s.is_secondary?'#666':'primary'" :item="s" small />
								</div>
							</div>

							<!-- sending supplemental info overwhelms the model, so we won't do this for now -->
							<div v-if="false && supplemental_info_available" class="mt-4">
								<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" style="margin:-4px 6px 0 0; font-size:18px;" color="light-blue" @click="evidence_help('supp_info')">fas fa-info-circle</k-icon></template>Click for more info</k-tooltip>
								<k-checkbox class="mt-0 pt-0 d-inline-block" style="vertical-align:bottom" color="pink darken-3" v-model="use_supp_info" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"><template v-slot:label><div class="black--text">Send “supplemental information” from standards</div></template></k-checkbox>
							</div>
						</div>
					</div>
				</div>
				
				<div v-if="all_standards.length == 0" class="lpc-pe-section">
					<div class="d-flex align-start">
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" style="margin:2px 6px 0 0; font-size:18px;" color="light-blue" @click="evidence_help('grade_level')">fas fa-info-circle</k-icon></template>Click for more info</k-tooltip>
						<div class="lpc-pe-section-description">Grade Level</div>
						<div>What grade level are you targeting for this lesson?</div>
						<k-spacer/>
						<div style="flex:0 0 120px" class="ml-3">
							<k-text-field outlined dense hide-details label="Grade Level" v-model="grade_level" placeholder=""></k-text-field>
						</div>
					</div>
				</div>

				<div v-if="suggestable_sections.length > 0" class="lpc-pe-section">
					<div class="d-flex align-start">
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" style="margin:2px 6px 0 0; font-size:18px;" color="light-blue" @click="evidence_help('sections')">fas fa-info-circle</k-icon></template>Click for more info</k-tooltip>
						<div class="lpc-pe-section-description">Other Sections</div>
						<div class="d-flex">
							<div v-for="(mc) in suggestable_sections" :key="mc.lc_uuid" class="mr-6">
								<k-checkbox class="mt-0 pt-0 d-inline-block" color="pink darken-3" off-icon="far fa-square" on-icon="fas fa-check-square" hide-details v-model="ss_selected[mc.lc_uuid]"><template v-slot:label><div class="black--text">{{mc.lc_title}}</div></template></k-checkbox>
							</div>
						</div>
					</div>
				</div>

				<div v-if="params.mdc.lc_gpt_add_time_to_prompt" class="lpc-pe-section">
					<div class="d-flex align-start">
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" style="margin:2px 6px 0 0; font-size:18px;" color="light-blue" @click="evidence_help('time')">fas fa-info-circle</k-icon></template>Click for more info</k-tooltip>
						<div class="lpc-pe-section-description">Time constraint</div>
						<div>Approximately how many minutes do you anticipate spending on this part of your lesson (optional)?</div>
						<k-spacer/>
						<div style="flex:0 0 120px" class="ml-3">
							<k-text-field outlined dense hide-details label="Minutes" v-model="time_constraint" placeholder=""></k-text-field>
						</div>
					</div>
				</div>

				<!-- this would explicitly invite teachers to add to the prompt, but for now don't show this; the teacher can preview and modify -->
				<div v-if="false" class="lpc-pe-section">
					<div class="d-flex align-center">
						<!-- <div class="lpc-pe-section-description">Extra instructions</div> -->
						<k-spacer/>
						<!-- <div style="font-size:14px">Click to specify additional instructions to send to the LLM:</div> -->
						<k-tooltip bottom><template v-slot:activator="{on}"><k-icon v-on="on" style="margin:2px 6px 0 0; font-size:18px;" color="light-blue" @click="evidence_help('extra')">fas fa-info-circle</k-icon></template>Click for more info</k-tooltip>
						<k-btn small text color="#333" class="k-tight-btn" dark @click="extra_instructions_showing=!extra_instructions_showing">Specify additional instructions to send to the LLM<k-icon small class="ml-2">fas fa-caret-down</k-icon></k-btn>
						<k-spacer/>
					</div>
					<k-textarea v-if="extra_instructions_showing" class="mt-2" outlined hide-details clearable auto-grow label="" placeholder="" v-model="extra_instructions" rows="2"></k-textarea>
				</div>

				<div class="lpc-pe-section text-center" style="margin-bottom:-8px">
					<k-btn v-show="alba_warning_issued" color="#000" small text class="mr-3 k-tight-btn" @click="show_prompt"><k-icon small class="mr-1">fas fa-eye</k-icon>Preview LLM Prompt</k-btn>
				</div>
			</div>
		</div>
	</k-card-text>
	<k-card-actions class="pa-3" style="border-top:1px solid #999">
		<k-btn color="#333" dark @click="$emit('dialog_cancel')"><k-icon small class="mr-2">fas fa-xmark</k-icon>Cancel</k-btn>
		<k-spacer/>
		<k-btn v-show="alba_warning_issued" color="pink darken-4" dark @click="proceed"><k-icon small class="mr-2">fas fa-robot</k-icon>Make Suggestions<k-icon small class="ml-2">fas fa-arrow-right</k-icon></k-btn>
	</k-card-actions>
</k-card></k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import CASEItemBtn from '../standards/CASEItemBtn'

export default {
	components: { CASEItemBtn },
	props: {
		params: { type: Object, required: true },
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		dialog_open: true,
		detail_select: [
			{value:'more', text: 'More detailed'},
			{value:'less', text: 'Less detailed'},
			{value:'same', text: 'About the same'}
		],
		topics_option_select: [
			{value:'none', text: 'Simple statements (“Students will…”)'},
			{value:'i_can', text: 'Success criteria (“I Can…” descriptors)'},
			{value:'questions', text: 'Essential questions to be answered'},
			{value:'blooms', text: 'Statements using Bloom’s action verbs'},
		],
		settings: {},
		extra_instructions_showing: false,
	}},
	computed: {
		...mapState([]),
		...mapGetters([]),
		alba_warning_issued: {
			get() { return this.$store.state.alba_warning_issued },
			set(val) { this.$store.commit('set', ['alba_warning_issued', val]) },
		},
		lpc_advanced_settings_showing: {
			get() { return this.$store.state.lpc_advanced_settings_showing },
			set(val) { this.$store.commit('set', ['lpc_advanced_settings_showing', val]) },
		},
		all_standards() {
			let arr = []
			if (this.$parent.primary_standards?.length > 0) {
				arr = arr.concat(this.$parent.primary_standards)
			}
			if (this.$parent.secondary_standards?.length > 0) {
				arr = arr.concat(this.$parent.secondary_standards)
			}
			return arr
		},
		supplemental_info_available() {
			return this.all_standards.findIndex(x=>!empty(x.extensions?.supplementalNotes)) > -1
		},

		// grade_level is cross-component
		grade_level: { 
			get() { return this.$parent.edited_lesson.lp_grade_level ?? '' },
			set(val) { this.$store.commit('set', [this.$parent.edited_lesson, 'lp_grade_level', val]) },
		},

		// these are settings that are specific to each component
		standards_selected: { 
			get() { return this.settings?.standards_selected ?? {} },
			set(val) { this.settings.standards_selected = val },
		},
		use_supp_info() { return false },	// see comment above
		// use_supp_info: { 
		// 	get() { return this.settings?.use_supp_info ?? false },
		// 	set(val) { this.settings.use_supp_info = val },
		// },
		ss_selected: { 
			get() { return this.settings?.ss_selected ?? {} },
			set(val) { this.settings.ss_selected = val },
		},
		detail_setting: { 
			get() { return this.settings?.detail_setting ?? 'same' },
			set(val) { this.settings.detail_setting = val },
		},
		time_constraint: { 
			get() { return this.settings?.time_constraint ?? '' },
			set(val) { this.settings.time_constraint = val },
		},
		extra_instructions: { 
			get() { return this.settings?.extra_instructions ?? '' },
			set(val) { this.settings.extra_instructions = val },
		},

		show_topics_options() {
			return this.params.component.lc_uuid == 'topics'
		},
		topics_option: { 
			get() { return this.settings?.topics_option ?? 'none' },
			set(val) { this.settings.topics_option = val },
		},

		lesson_master() { return this.$parent.lesson_master },
		lesson_plan() { return this.$parent.lesson_plan },

		suggestable_sections() {
			let arr = []
			for (let mc of this.lesson_master.lesson_plan) {
				// skip the current component
				if (mc.lc_uuid == this.params.component.lc_uuid) continue

				// if this master component has a lc_gpt_messages_start value...
				if (!empty(mc.lc_gpt_messages_start)) {
					// then if we have content for this component, add the lc_gpt_messages_start value, subbing the component's content
					let c = this.lesson_plan.find(x=>x.lc_uuid==mc.lc_uuid)
					if (!empty(c?.lc_content)) {
						arr.push(mc)
					}
				}
			}
			return arr
		},
		
		llm_prompt() {
			let component = this.params.component
			let mdc = this.params.mdc
			let standards = this.all_standards
			let supp_notes_phrase = ''

			// according to ChatGPT (11/10/2025), this “gives the model a persona (making behavior more stable)”
			let messages = `You are an educational assistant helping teachers create a lesson plan for use with their students.\n\n`

			// start with messages for primary_standards and grades if we have them
			if (standards.length > 0) {
				// compose phrases about the primary_standards and grades being taught to send to gpt
				let grade_phrase = '', standards_phrase = ''
				let grade_low = 100
				let grade_high = -10
				let sn_count = 0
				for (let i = 0; i < standards.length; ++i) {
					let s = standards[i]

					if (U.grade_value(s.grade_low) < grade_low) grade_low = U.grade_value(s.grade_low)
					if (U.grade_value(s.grade_high) > grade_high) grade_high = U.grade_value(s.grade_high)

					// skip standards the user doesn't want to include
					if (!this.standards_selected[s.identifier]) continue
					
					standards_phrase += `\n- ${U.html_to_text(s.fullStatement)}`

					// create phrase from the first supplementalNotes if we have them and the user says to use them
					let sn = s.extensions?.supplementalNotes || ''
					if (this.use_supp_info && !empty(sn) && empty(supp_notes_phrase)) {
						if (sn_count == 0) {
							supp_notes_phrase = '\n\nADDITIONAL GUIDANCE FROM STANDARDS:\nTake the following guidance into account when making suggestions:\n\n'
						} else {
							supp_notes_phrase += '\n\n'
						}
						supp_notes_phrase += U.html_to_text_with_spacing(sn)
						++sn_count	// in case we later want to use more than one of these...
					}
				}

				grade_low = vapp.$store.state.grades[grade_low]		// todo: spell out "pre-kindergarten" and "kindergarten"
				grade_high = vapp.$store.state.grades[grade_high]
				if (grade_low || grade_high) {
					if (grade_low == grade_high) grade_phrase = 'Grade ' + grade_low
					else grade_phrase = `Grades ${grade_low} to ${grade_high}`
				}

				messages += `The lesson will be used with **${grade_phrase} students**`

				if (standards.length == 1) {
					messages += `, and should address this learning standard: ${standards_phrase}`
				} else if (standards.length > 1) {
					messages += `, and should address these learning standards: ${standards_phrase}`
				}
				messages += `\n\n`

			} else if (!empty(this.grade_level)) {
				this.grade_level = $.trim(this.grade_level)
				if (!empty(this.grade_level)) {
					messages += `The lesson will be used with **Grade ${this.grade_level} students**.\n\n`
				}
			}

			//
			let extra_task_phrase = ''
			if (this.params.component.lc_uuid == 'topics') {
				if (this.settings.topics_option == 'i_can') {
					extra_task_phrase = 'Format the text of each suggestion as a *success criterion* statement starting with “I can”.'
				} else if (this.settings.topics_option == 'questions') {
					extra_task_phrase = 'Format the text of each suggestion as an *essential question* students should be able to answer.'
				} else if (this.settings.topics_option == 'blooms') {
					extra_task_phrase = 'Format the text of each suggestion as a statement that *starts with a Bloom’s action verb*—recognize, understand, apply, analyze, evaluate, or create.'
				} else {
					extra_task_phrase = 'Format the text of each suggestion as a statement starting with “Students will”.'
				}
			}

			// generate the main prompt
			let n_suggestions = this.$parent.default_suggestions_to_request
			if (component.lc_uuid == 'vocabulary') {
				n_suggestions = `${this.$parent.default_min_vocab_suggestions}-${this.$parent.default_max_vocab_suggestions}`
			}
			let prompt = `## CORE TASK (do this first)\n Generate ${n_suggestions} suggested ${mdc.lc_gpt_messages_prompt}. ${extra_task_phrase} All suggestions must be approximately the same length.`

			// deal with time_constraint
			if (!empty(this.time_constraint)) {
				prompt += ` The teacher plans to spend approximately **${this.time_constraint} minutes** on this part of the lesson.`
			}

			messages += this.$parent.message_text(prompt, component, mdc) + `\n\n`

			// if the teacher has added something already, say to make new suggestions different but related to what they've said
			let current_content_constraint = ''
			if (!empty(component.lc_content)) {
				current_content_constraint = `Suggestions you make should **differ from**, but be **related to**, the following ${mdc.lc_title_plural}:\n\n${U.html_to_text_with_spacing(component.lc_content)}\n\n`
			}
			messages += current_content_constraint

			// if the teacher has already asked for suggestions...
			let previous_suggestions_constraint = ''
			if (component.lc_suggestion_length > 0) {
				// if they're asking for the same level of detail
				if (this.detail_setting == 'same') {
					// send through the summaries of previous suggestions and ask for different ones at about the same length as last time
					if (component.lc_generated_suggestion_summaries.length > 0) {
						previous_suggestions_constraint += `\n- Suggestions you make should **differ** from the following previously-made suggestions: “${component.lc_generated_suggestion_summaries.join('”, “')}”`
					}

				// else they want more or less detail
				} else {
					// so just specify a new length, but don't say anything about what the previous suggestions were
					let wc = (this.detail_setting == 'more') ? Math.ceil(component.lc_suggestion_length * 1.5) : Math.ceil(component.lc_suggestion_length * 0.75)
					this.$store.commit('set', [component, 'lc_suggestion_length', wc])
				}
			}
			// NOTE: make_component_suggestions2 will modify the schema to specify the exact word length constraints

			// now add messages about components other than the current component that we've already filled in
			// for each component in the master lesson plan...
			let additional_constraints = ''
			for (let mc of this.suggestable_sections) {
				// don't include sections that are unchecked
				if (!this.ss_selected[mc.lc_uuid]) continue

				let c = this.lesson_plan.find(x=>x.lc_uuid==mc.lc_uuid)
				additional_constraints += `\n- ` + this.$parent.message_text(mc.lc_gpt_messages_start, c, mdc)
			}

			messages += `## REQUIRED CONSTRAINTS (apply AFTER completing the core task)`
			messages += additional_constraints
			messages += previous_suggestions_constraint

			// add extra_instructions if we got any (not currently implemented, but could be brought back later)
			if (!empty(this.extra_instructions)) {
				messages += `\n\n## ADDITIONAL TEACHER INSTRUCTIONS\nThe teacher also asks that you consider the following when making suggestions:\n\n${this.extra_instructions}`
			}

			// add supp_notes_phrase if we created one above (not currently implemented, but could be brought back later)
			messages += supp_notes_phrase
				
			return messages
		},
		/* This would try to find accepted suggestions that might have been edited...
				let n_summaries = 0
				$(`<div>${component.lc_content}</div>`).find('[data-summary]').each((i, el) => {
					if (n_summaries > 0) previous_suggestions += `, `
					previous_suggestions += $(el).text()
					++n_summaries
				})
		*/
	},
	watch: {
		settings: { deep:true, handler() {
			this.$store.commit('set', [this.params.component, 'lc_prompt_settings', this.settings])
		}}
	},
	created() {
		vapp.lpc_prompt_editor_component = this

		// construct/reconstruct settings for this component		
		let settings = {}
		let os = this.params.component.lc_prompt_settings
		// if (empty(settings.use_supp_info)) settings.use_supp_info = false
		sdp(settings, os, 'detail_setting', 'same')
		sdp(settings, os, 'grade_level', '')
		sdp(settings, os, 'time_constraint', '')
		sdp(settings, os, 'extra_instructions', '')
		if (!empty(settings.extra_instructions)) this.extra_instructions_showing = true

		// standards
		let o = {}
		for (let i = 0; i < this.all_standards.length; ++i) {
			let id = this.all_standards[i].identifier
			if (os.standards_selected && !empty(os.standards_selected[id])) {
				o[id] = os.standards_selected[id]
			} else {
				o[id] = (i == 0)
			}
		}
		settings.standards_selected = o

		// suggestable sections
		o = {}
		for (let i = 0; i < this.suggestable_sections.length; ++i) {
			let id = this.suggestable_sections[i].lc_uuid
			if (os.ss_selected && !empty(os.ss_selected[id])) {
				o[id] = os.ss_selected[id]
			} else {
				o[id] = true
			}
		}
		settings.ss_selected = o

		// the topics component has a special setting
		if (this.params.component.lc_uuid == 'topics') {
			if (!empty(os.topics_option)) {
				settings.topics_option = os.topics_option
			} else if (!empty(this.params.mdc.lc_topics_option)) {
				settings.topics_option = this.params.mdc.lc_topics_option
			} else {
				settings.topics_option = ''
			}
		}

		// set a local copy of this; we save it to the component via a watcher
		this.settings = settings
	},
	mounted() {
	},
	methods: {
		show_prompt() {
			let text = this.llm_prompt.replace(/(.*)/g, '<div class="mb-2">$1</div>')

			this.$confirm({
				title: 'LLM Prompt Preview',
				text: `<div style="font-family:monospace; font-size:14px; line-height:18px">${text}</div>`,
				acceptText: 'Back   ',
				acceptIcon: 'fas fa-arrow-left',
				acceptColor: '#333',
				cancelText: 'Modify Prompt',
				cancelColor: 'pink darken-4',
				dialogMaxWidth: 800,
			}).then(y => {
				// OK click means don't modify the prompt
			}).catch(n=>{
				// Cancel click means modify the prompt
				this.$prompt({
					title: 'Modify LLM Prompt',
					text: '<div class="mb-3">Modify the prompt below, then click SEND PROMPT to send it to the LLM.</div>',
					promptType: 'textarea',		// default is 'text'
					initialValue: this.llm_prompt,
					acceptText: 'Send Prompt',
					acceptIcon: 'fas fa-robot',
					acceptIconAfter: 'fas fa-circle-arrow-right',
					acceptColor: 'pink darken-4',
					cancelText: 'Cancel (Don’t Modify Prompt)',
					cancelIcon: 'fas fa-xmark',
					dialogMaxWidth: 800,
				}).then(modified_prompt => {
					modified_prompt = $.trim(modified_prompt)
					this.$emit('proceed', this.params.component, modified_prompt)
					this.$emit('dialog_cancel')
				}).catch(n=>{
					this.show_prompt()
				}).finally(f=>{})
				
			}).finally(f=>{})
		},

		proceed() {
			// always ask for grade level if we need one
			if (this.all_standards.length == 0) {
				this.grade_level = $.trim(this.grade_level)
				if (empty(this.grade_level)) {
					this.$prompt({
						title: 'Grade Level',
						text: 'Enter the grade level your lesson is targeting:',
						initialValue: '',
						acceptText: 'Proceed',
						acceptIconAfter: 'fas fa-circle-arrow-right',
						dialogMaxWidth: 440,
					}).then(grade => {
						grade = $.trim(grade)
						if (!empty(grade)) {
							this.$store.commit('set', ['grade_level', grade])
							this.proceed()
						}
					}).finally(f=>{})
					return
				}
			}

			this.$emit('proceed', this.params.component, this.llm_prompt)
			this.$emit('dialog_cancel')
		},

		show_standard(s) {
			this.$parent.show_standard(s)
		},

		evidence_help(which) {
			let msg, title
			if (which == 'standards') {
				title = 'Standards'
				msg = `<p>Select the <b>standards</b> used when requesting suggestions.</p>`
					+ `<p>By default, the first-listed standard is used; including additional standards may overwhelm other sources of information the LLM considers when making suggestions.</p>`
			} else if (which == 'supp_info') {	// not currently used; see comment above
				msg = `<p></p>`
					+ `<p></p>`
			} else if (which == 'grade_level') {
				title = 'Grade Level'
				msg = `<p>Specify the grade level you are targeting for this lesson.</p>`
					+ `<p>Note that if you specify one or more learning standards for the lesson, the LPC will automatically target the lesson to the grade levels associated with those standards.</p>`
			} else if (which == 'sections') {
				title = 'Other Sections'
				msg = `<p>By default, the LPC will send to the LLM the text you’ve already entered for some <b>previous sections</b> when requesting suggestions for this section. Uncheck the boxes of any sections you <i>don’t</i> want to be included in the prompt.</p>`
			} else if (which == 'detail') {
				title = 'Level of Detail'
				msg = `<p>If you specify that new suggestions should be <b>more detailed</b> than previous suggestions, the LPC will ask the LLM to make new suggestions that are 1.5 times longer than the last set of suggestions.</p>`
					+ `<p>If you specify that new suggestions should be <b>less detailed</b> than previous suggestions, the LPC will ask the LLM to make new suggestions that are 0.75 times as long as the last set of suggestions.</p>`
					+ `<p>If you specify that new suggestions should be <b>about the same</b> as previous suggestions, the LPC will ask the LLM to make new suggestions that are about the same length as the last set of suggestions, <b>and the LPC will explicitly ask LLM to make new suggestions that <i>differ</i> from previous ones</b>.</p>`
			} else if (which == 'time') {
				title = 'Time constraint'
				msg = `<p>If you wish, specify the amount of time you plan to spend on this section, and the LPC will pass this time constraint in to the LLM when asking for suggestions.</p>`
			} else if (which == 'extra') {	// not currently used
				msg = `<p></p>`
					+ `<p></p>`
			} else if (which == 'topics_option') {
				title = 'Objectives Format'
				msg = `<p>Choose how you would like suggested learning outcomes/topics to be formatted:</p>`
					+ `<ul>`
					// 'none'
					+ `<li><b>Simple statements:</b> Format the text of each suggestion as a statement starting with “Students will”.</li>`
					// 'i_can'
					+ `<li><b>Success criteria:</b> Format the text of each suggestion as a success criterion statement starting with “I can”.</li>`
					// 'questions'
					+ `<li><b>Essential questions:</b> Format the text of each suggestion as an essential question students should be able to answer.</li>`
					// 'blooms'
					+ `<li><b>Bloom’s action verbs:</b> Format the text of each suggestion as a statement that starts with a Bloom’s action verb—recognize, understand, apply, analyze, evaluate, or create.</li>`
					+ `</ul>`

			}
			this.$alert({
				title: title,
				text: msg,
				dialogMaxWidth: 600,
			}).then(y => {})
		},
	}
}
</script>

<style lang="scss">
.lpc-pe-alba-warning {
	background-color:$v-pink-lighten-5;
	padding:1px 12px 12px 12px;
	border-radius:8px;
}

.lpc-pe-section {
	border-top:1px solid #ccc;
	padding-top:12px;
	margin-top:12px;
}

.lpc-pe-section:first-of-type { 
	border-top:0; 
	padding-top:0;
	margin-top:0;
}

.lpc-pe-section-description {
	flex: 0 0 100px;
	font-weight:bold;
	font-size:14px;
	line-height:18px;
	white-space: nowrap;
	margin-right:16px;
	margin-top:2px;
}
</style>
